<template>
<div class="cc-root" :style="themePageStyle">
  <nav class="cc-topbar"><div class="container d-flex align-items-center justify-content-between py-2">
    <div class="d-flex align-items-center" style="gap:12px;">
      <a href="javascript:void(0)" @click="$router.go(-1)" class="cc-back"><i class="mdi mdi-arrow-left"></i></a>
      <img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:32px;" />
      <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:14px;">Cart Checkout</span>
    </div>
  </div></nav>

  <div class="container cc-container">
    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

    <!-- ===== POST-CHECKOUT (orders created, payment in progress / retry / done) ===== -->
    <div v-else-if="success" class="cc-post">
      <!-- Order summary always visible at top -->
      <div class="cc-success-card text-center mb-3">
        <i class="mdi mdi-receipt-text-check" style="font-size:54px;color:#BFA164;"></i>
        <h4 class="mt-2" style="color:var(--fm-h,#F5F0E8);font-weight:700;">Orders created</h4>
        <p style="color:var(--fm-s,#6B6356);font-size:13px;margin-bottom:14px;">{{ success.orders.length }} order{{ success.orders.length===1?'':'s' }} from {{ success.sellerCount }} seller{{ success.sellerCount===1?'':'s' }} · Total <strong style="color:#BFA164;">KES {{ fNum(success.amount) }}</strong></p>
        <div style="font-size:11px;color:#8A827A;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px;">Order group</div>
        <div style="color:#BFA164;font-family:monospace;font-size:13px;margin-bottom:14px;">{{ success.orderGroupId }}</div>
        <div v-for="o in success.orders" :key="o.id" class="cc-success-line">
          <span><span class="cc-mono">{{ o.order_number }}</span> · {{ o.title }}</span>
          <span>KES {{ fNum(o.amount) }}</span>
        </div>
      </div>

      <!-- ===== Payment status panel ===== -->
      <div class="cc-card cc-pay-panel">

        <!-- Split-payment header: only shown when total was split into > 1 chunk -->
        <div v-if="splitTotal > 1" style="background:rgba(191,161,100,0.08);border:1px solid rgba(191,161,100,0.3);border-radius:8px;padding:12px 14px;margin-bottom:14px;">
          <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:6px;">
            <div style="color:#BFA164;font-weight:600;font-size:13px;"><i class="mdi mdi-call-split me-1"></i>Split payment</div>
            <div style="color:var(--fm-m,#A09888);font-size:11px;">{{ splitSeq }} of {{ splitTotal }}</div>
          </div>
          <div style="color:var(--fm-h,#F5F0E8);font-size:12px;line-height:1.5;">
            Your total <strong>KES {{ fNum(totalDue) }}</strong> is being paid in {{ splitTotal }} M-Pesa prompts (the per-payment limit is KES {{ fNum(stkCeiling) }}).
          </div>
          <!-- progress bar -->
          <div style="background:rgba(255,255,255,0.06);border-radius:10px;height:8px;margin-top:10px;overflow:hidden;">
            <div :style="{width: paidPercent + '%', background:'#50C878', height:'100%', transition:'width .3s'}"></div>
          </div>
          <div style="display:flex;justify-content:space-between;margin-top:6px;font-size:11px;color:var(--fm-m,#A09888);">
            <span>Paid: <strong style="color:#50C878;">KES {{ fNum(paidSoFar) }}</strong></span>
            <span>Outstanding: <strong style="color:var(--fm-h,#F5F0E8);">KES {{ fNum(Math.max(0, totalDue - paidSoFar)) }}</strong></span>
          </div>
        </div>

        <!-- Waiting -->
        <div v-if="payStatus === 'pending' || payStatus === 'stk_sent'" class="text-center">
          <div class="cc-pulse-row">
            <div class="cc-pulse"></div>
            <span style="color:#50C878;font-weight:600;">Waiting for M-Pesa PIN<span v-if="splitTotal > 1"> — payment {{ splitSeq }} of {{ splitTotal }}</span></span>
          </div>
          <p style="color:var(--fm-m,#A09888);font-size:13px;margin:0 0 10px;">
            Check your phone <strong style="color:var(--fm-h,#F5F0E8);">{{ payPhone }}</strong> and enter your PIN for
            <strong style="color:#BFA164;">KES {{ fNum(amountThisChunk || success.amount) }}</strong>.
          </p>
          <div style="color:var(--fm-s,#6B6356);font-size:12px;">
            <i class="mdi mdi-refresh mdi-spin me-1"></i>Checking payment status... ({{ pollCountdown }}s)
          </div>
        </div>

        <!-- Chunk succeeded but more chunks to go (split flow only) -->
        <div v-else-if="payStatus === 'chunk_succeeded' && awaitingNextChunk" class="text-center">
          <i class="mdi mdi-check-circle" style="font-size:42px;color:#50C878;"></i>
          <h6 style="color:#50C878;font-weight:700;margin-top:8px;">Payment {{ splitSeq - 1 }} received</h6>
          <p style="color:var(--fm-m,#A09888);font-size:13px;margin:0 0 14px;">
            One down, {{ splitTotal - splitSeq + 1 }} to go.
            Outstanding: <strong style="color:var(--fm-h,#F5F0E8);">KES {{ fNum(totalDue - paidSoFar) }}</strong>.
          </p>
          <button class="btn btn-warning fw-bold btn-lg" @click="payNext" :disabled="payingNext">
            <span v-if="payingNext"><span class="spinner-border spinner-border-sm me-2"></span>Sending next prompt…</span>
            <span v-else>
              <i class="mdi mdi-cellphone-arrow-down me-1"></i>
              Send payment {{ splitSeq }} of {{ splitTotal }} — KES {{ fNum(amountThisChunk) }}
            </span>
          </button>
        </div>

        <!-- Completed (only when fully paid, not a partial chunk) -->
        <div v-else-if="payStatus === 'success' || payStatus === 'completed'" class="text-center">
          <i class="mdi mdi-check-circle" style="font-size:54px;color:#50C878;"></i>
          <h5 style="color:#50C878;font-weight:700;margin-top:10px;">Payment confirmed!</h5>
          <p v-if="mpesaReceipt" style="color:var(--fm-m,#A09888);font-size:13px;margin:0;">M-Pesa Receipt: <strong style="color:var(--fm-h,#F5F0E8);">{{ mpesaReceipt }}</strong></p>
          <p v-if="splitTotal > 1" style="color:#50C878;font-size:13px;margin-top:6px;font-weight:600;">All {{ splitTotal }} payments received — total KES {{ fNum(totalDue) }} in escrow.</p>
          <p style="color:var(--fm-m,#A09888);font-size:13px;margin-top:6px;">Each order is now in escrow. Sellers have been notified. A Flockr coordinator will contact you within 24h about transport.</p>
        </div>

        <!-- Failed / Cancelled / Timeout — show retry -->
        <div v-else-if="payStatus === 'failed' || payStatus === 'cancelled' || payStatus === 'timeout'" class="text-center">
          <i class="mdi mdi-close-circle" style="font-size:48px;color:#FF6B6B;"></i>
          <h5 style="color:#FF6B6B;font-weight:700;margin-top:8px;">{{ payStatus === 'cancelled' ? 'Payment cancelled' : (payStatus === 'timeout' ? 'No PIN entered in time' : 'Payment failed') }}</h5>
          <p style="color:var(--fm-m,#A09888);font-size:13px;margin:0 0 14px;">{{ payFailReason || 'You can resend the prompt — to the same phone or a different one.' }}</p>

          <div class="cc-retry-box">
            <h6 style="color:var(--fm-h,#F5F0E8);font-size:13px;font-weight:600;margin-bottom:10px;">Try again</h6>
            <div class="d-flex gap-2 align-items-center justify-content-center mb-2">
              <span style="color:var(--fm-s,#6B6356);font-size:14px;font-weight:500;">+254</span>
              <input v-model="retryPhone" type="tel" class="cc-input" placeholder="7XX XXX XXX" style="max-width:240px;" maxlength="10" />
            </div>
            <button class="btn btn-warning fw-bold" @click="resendStk" :disabled="retryingStk || !retryPhoneValid">
              <span v-if="retryingStk"><span class="spinner-border spinner-border-sm me-1"></span>Sending…</span>
              <span v-else><i class="mdi mdi-cellphone-wireless me-1"></i>Resend M-Pesa Prompt</span>
            </button>
          </div>
        </div>

        <!-- Paybill fallback (always visible until completed) -->
        <div v-if="payStatus !== 'success' && payStatus !== 'completed'" class="cc-paybill mt-4">
          <h6 style="color:var(--fm-m,#A09888);font-size:13px;font-weight:600;margin-bottom:10px;"><i class="mdi mdi-bank me-1"></i>Or pay manually via M-Pesa Paybill</h6>
          <div class="cc-paybill-grid">
            <div><span class="cc-pb-lbl">Paybill</span><span class="cc-pb-val" style="color:#BFA164;">4160303</span></div>
            <div><span class="cc-pb-lbl">Account</span><span class="cc-pb-val">{{ accountRef }}</span></div>
            <div><span class="cc-pb-lbl">Amount</span><span class="cc-pb-val" style="color:#50C878;">KES {{ fNum(totalDue || success.amount) }}</span><span v-if="splitTotal > 1 && paidSoFar > 0" style="font-size:10px;color:var(--fm-m,#A09888);display:block;">Outstanding: KES {{ fNum(totalDue - paidSoFar) }}</span></div>
          </div>
          <p style="color:var(--fm-s,#6B6356);font-size:11px;margin-top:8px;"><i class="mdi mdi-information-outline me-1"></i>M-Pesa &gt; Lipa na M-Pesa &gt; Pay Bill &gt; enter the details above</p>
          <button class="btn btn-sm btn-outline-warning mt-1" @click="copyPayLink(success.orderGroupId)"><i class="mdi" :class="payLinkCopied === success.orderGroupId ? 'mdi-check' : 'mdi-link-variant'"></i> {{ payLinkCopied === success.orderGroupId ? 'Link copied!' : 'Copy payment link' }}</button>
          <p style="color:var(--fm-s,#6B6356);font-size:10px;margin-top:4px;">Share this link with anyone paying on your behalf — no login needed.</p>
        </div>
      </div>

      <!-- Footer actions -->
      <div class="d-flex gap-2 justify-content-center mt-4 flex-wrap">
        <button class="btn btn-outline-warning" @click="$router.push('/orders')"><i class="mdi mdi-receipt me-1"></i>View My Orders</button>
        <button class="btn btn-outline-secondary" @click="$router.push('/marketplace')"><i class="mdi mdi-storefront me-1"></i>Back to Marketplace</button>
      </div>
    </div>

    <!-- ===== EMPTY ===== -->
    <div v-else-if="!groups.length" class="text-center py-5">
      <i class="mdi mdi-cart-off" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
      <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">Your cart is empty</h5>
      <button class="btn btn-warning mt-2" @click="$router.push('/marketplace')"><i class="mdi mdi-magnify me-1"></i>Find Animals</button>
    </div>

    <!-- ===== CHECKOUT FORM ===== -->
    <div v-else>
      <div v-if="error" class="cc-alert">{{ error }}</div>

      <div class="row g-4">
        <!-- LEFT: Order Summary -->
        <div class="col-lg-7">
          <h5 style="color:var(--fm-h,#F5F0E8);font-weight:700;margin-bottom:16px;">{{ itemCount }} item{{ itemCount===1?'':'s' }} from {{ groups.length }} seller{{ groups.length===1?'':'s' }}</h5>

          <div v-for="g in groups" :key="g.seller_id" class="cc-group">
            <div class="cc-group-hd">
              <div class="d-flex align-items-center gap-2" style="min-width:0;">
                <div class="cc-avatar"><img v-if="g.seller_avatar" :src="avatarUrl(g.seller_avatar)" /><span v-else>{{ (g.seller_name||'S').charAt(0) }}</span></div>
                <div style="min-width:0;">
                  <div style="color:var(--fm-h,#F5F0E8);font-weight:600;font-size:13px;">{{ g.seller_name }}</div>
                  <div style="color:var(--fm-s,#6B6356);font-size:11px;">{{ g.items.length }} item{{ g.items.length===1?'':'s' }} · subtotal KES {{ fNum(g.subtotal) }}</div>
                </div>
              </div>
            </div>
            <div v-for="it in g.items" :key="it.cart_item_id" class="cc-item" :style="(it._paid || it._pending) ? 'opacity:0.55;' : ''">
              <div style="flex:1;min-width:0;">
                <div style="color:var(--fm-h,#F5F0E8);font-size:13px;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
                  {{ it.title }}
                  <span v-if="it._paid" style="color:#50C878;margin-left:6px;font-size:11px;">✓ Paid</span>
                  <span v-else-if="it._pending" style="color:#FFC107;margin-left:6px;font-size:11px;"><i class="mdi mdi-clock-outline"></i> Awaiting payment</span>
                </div>
                <div style="color:var(--fm-s,#6B6356);font-size:11px;">{{ it.quantity }} × KES {{ fNum(it.unit_price) }}</div>
              </div>
              <div v-if="canEditQty(it) && !it._paid && !it._pending" class="cc-qty">
                <button type="button" @click="changeQty(it, Number(it.quantity) - 1)" :disabled="qtyBusy || Number(it.quantity) <= 1"><i class="mdi mdi-minus"></i></button>
                <span>{{ it.quantity }}</span>
                <button type="button" @click="changeQty(it, Number(it.quantity) + 1)" :disabled="qtyBusy || (it.available_quantity && Number(it.quantity) >= Number(it.available_quantity))"><i class="mdi mdi-plus"></i></button>
              </div>
              <div style="color:#BFA164;font-weight:600;font-size:13px;">KES {{ fNum(Number(it.unit_price) * Number(it.quantity)) }}</div>
            </div>

            <!-- Client issue 28: buyer picks animal transport per seller instead of a forced "Flockr will arrange" banner. -->
            <div v-if="g.hasListings" style="padding:10px 12px;border-top:1px solid var(--fm-br,#1E1E1E);">
              <div style="font-size:11px;font-weight:600;color:#BFA164;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:8px;"><i class="mdi mdi-truck-fast me-1"></i>Animal transport</div>
              <label style="display:flex;align-items:flex-start;gap:8px;cursor:pointer;font-size:13px;color:var(--fm-h,#F5F0E8);margin-bottom:8px;">
                <input type="radio" :name="'tr-'+g.seller_id" value="flockr_arranges" v-model="groupTransport[g.seller_id]" style="accent-color:#BFA164;margin-top:3px;flex-shrink:0;">
                <span><i class="mdi mdi-truck-fast me-1" style="color:#BFA164;"></i>Flockr arranges transport<br>
                  <span style="color:var(--fm-s,#6B6356);font-size:11px;">We contact you within 24h to organise pickup &amp; delivery. Invoiced separately.</span></span>
              </label>
              <label style="display:flex;align-items:flex-start;gap:8px;cursor:pointer;font-size:13px;color:var(--fm-h,#F5F0E8);margin-bottom:0;">
                <input type="radio" :name="'tr-'+g.seller_id" value="buyer_collects" v-model="groupTransport[g.seller_id]" style="accent-color:#BFA164;margin-top:3px;flex-shrink:0;">
                <span><i class="mdi mdi-account-arrow-right me-1" style="color:#50C878;"></i>I will collect from the seller<br>
                  <span style="color:var(--fm-s,#6B6356);font-size:11px;">Free — pick up the animals yourself after payment.</span></span>
              </label>
            </div>

            <!-- Per-seller totals -->
            <div v-if="groupTotals[g.seller_id]" style="padding:10px 12px;border-top:1px solid var(--fm-br,#1E1E1E);font-size:12px;">
              <div class="d-flex justify-content-between mb-1" style="color:var(--fm-s,#6B6356);">
                <span>Items subtotal</span><span>KES {{ fNum(groupTotals[g.seller_id].subtotal) }}</span>
              </div>
              <div v-if="groupTotals[g.seller_id].insurance > 0" class="d-flex justify-content-between mb-1" style="color:#50C878;">
                <span><i class="mdi mdi-shield-check me-1"></i>Insurance</span><span>KES {{ fNum(groupTotals[g.seller_id].insurance) }}</span>
              </div>
              <div v-if="groupTotals[g.seller_id].delivery > 0" class="d-flex justify-content-between mb-1" style="color:#4FC3F7;">
                <span><i class="mdi mdi-truck-delivery me-1"></i>Seller delivery</span><span>KES {{ fNum(groupTotals[g.seller_id].delivery) }}</span>
              </div>
              <div v-if="g.hasListings && (groupTransport[g.seller_id]||'flockr_arranges')==='flockr_arranges'" class="d-flex justify-content-between mb-1" style="color:#FF9800;font-size:11px;">
                <span><i class="mdi mdi-information-outline me-1"></i>Animal transport</span><span>Invoiced separately</span>
              </div>
              <div v-else-if="g.hasListings" class="d-flex justify-content-between mb-1" style="color:#50C878;font-size:11px;">
                <span><i class="mdi mdi-account-arrow-right me-1"></i>Animal transport — you collect</span><span>Free</span>
              </div>
              <div class="d-flex justify-content-between mt-2 pt-2" style="border-top:1px dashed var(--fm-br,#1E1E1E);color:var(--fm-h,#F5F0E8);font-weight:700;font-size:14px;">
                <span>Pay {{ g.seller_name }}</span>
                <span style="color:#BFA164;">KES {{ fNum(groupTotals[g.seller_id].total) }}</span>
              </div>
            </div>

            <!-- Per-seller status + Checkout button -->
            <div style="padding:10px 12px;">
              <!-- Payment method picker — only shows when buyer has an on-account relationship with this seller -->
              <div v-if="g.corporate_terms" class="mb-3" style="padding:10px 12px;background:rgba(80,200,120,.08);border:1px solid rgba(80,200,120,.25);border-radius:8px;">
                <div style="font-size:11px;font-weight:600;color:#50C878;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;"><i class="mdi mdi-handshake me-1"></i>You have an on-account relationship with {{ g.seller_name }}</div>
                <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:13px;color:var(--fm-h,#F5F0E8);margin-bottom:4px;">
                  <input type="radio" :name="'pm-'+g.seller_id" :value="'mpesa'" v-model="groupPayMethod[g.seller_id]" style="accent-color:#BFA164;">
                  <i class="mdi mdi-cellphone-wireless" style="color:#50C878;"></i> Pay now with M-Pesa STK
                </label>
                <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:13px;color:var(--fm-h,#F5F0E8);">
                  <input type="radio" :name="'pm-'+g.seller_id" :value="'on_account'" v-model="groupPayMethod[g.seller_id]" style="accent-color:#BFA164;">
                  <i class="mdi mdi-file-document-outline" style="color:#BFA164;"></i> On account ({{ g.corporate_terms.payment_terms_days }}-day RTGS as <strong>{{ g.corporate_terms.company_name }}</strong>)
                </label>
              </div>
              <!-- Status pill -->
              <div v-if="groupState(g.seller_id).status === 'stk_sent'" class="mb-2" style="padding:8px 10px;background:rgba(255,193,7,.12);border-radius:8px;color:#FFC107;font-size:12px;">
                <i class="mdi mdi-cellphone-message me-1"></i>STK push sent to your phone — enter your M-Pesa PIN
              </div>
              <div v-else-if="groupState(g.seller_id).status === 'success'" class="mb-2" style="padding:8px 10px;background:rgba(80,200,120,.12);border-radius:8px;color:#50C878;font-size:12px;">
                <i class="mdi mdi-check-circle me-1"></i>Paid · M-Pesa ref {{ groupState(g.seller_id).mpesaReceipt || '—' }}
              </div>
              <div v-else-if="groupState(g.seller_id).status === 'invoiced'" class="mb-2" style="padding:8px 10px;background:rgba(80,200,120,.12);border-radius:8px;color:#50C878;font-size:12px;">
                <i class="mdi mdi-file-document-check me-1"></i>Invoice <strong>{{ groupState(g.seller_id).invoiceNumber || '—' }}</strong> issued · KES {{ fNum(groupState(g.seller_id).invoiceTotal || 0) }} due {{ groupState(g.seller_id).dueDate || 'soon' }}
              </div>
              <div v-else-if="groupState(g.seller_id).status === 'failed' || groupState(g.seller_id).status === 'cancelled' || groupState(g.seller_id).status === 'timeout'" class="mb-2" style="padding:8px 10px;background:rgba(255,107,107,.12);border-radius:8px;color:#FF6B6B;font-size:12px;">
                <i class="mdi mdi-alert-circle me-1"></i>{{ groupState(g.seller_id).payFailReason || 'Payment did not go through' }}
              </div>
              <div v-else-if="groupState(g.seller_id).status === 'chunk_succeeded'" class="mb-2" style="padding:8px 10px;background:rgba(255,193,7,.12);border-radius:8px;color:#FFC107;font-size:12px;">
                <i class="mdi mdi-progress-clock me-1"></i>{{ groupState(g.seller_id).paidSoFar }} of {{ groupState(g.seller_id).totalDue }} paid · tap below for the next M-Pesa prompt ({{ groupState(g.seller_id).splitSeq + 1 }} of {{ groupState(g.seller_id).splitTotal }})
              </div>

              <!-- Checkout / Retry / Next button -->
              <button v-if="groupState(g.seller_id).status === 'chunk_succeeded'"
                class="btn btn-warning w-100"
                @click="payNextGroupChunk(g.seller_id)">
                <i class="mdi mdi-cellphone-wireless me-1"></i>Send next M-Pesa prompt — KES {{ fNum(groupState(g.seller_id).amountThisChunk) }}
              </button>
              <button v-else
                class="btn w-100"
                :class="groupBtnClass(g)"
                :disabled="groupBtnDisabled(g)"
                @click="submitGroup(g)">
                <span v-if="groupState(g.seller_id).status === 'submitting' || groupState(g.seller_id).status === 'stk_sent'" class="spinner-border spinner-border-sm me-1"></span>
                <i v-else-if="groupState(g.seller_id).status === 'success'" class="mdi mdi-check me-1"></i>
                <i v-else class="mdi mdi-cellphone-wireless me-1"></i>
                {{ groupBtnLabel(g) }}
              </button>

              <!-- Admin-portal #23: populated manual-paybill fallback — account number
                   was never shown for cart orders, so buyers couldn't finish paying
                   manually when the STK prompt failed or timed out. -->
              <div v-if="groupState(g.seller_id).orderGroupId && ['stk_sent','failed','cancelled','timeout','chunk_succeeded'].indexOf(groupState(g.seller_id).status) > -1"
                class="mt-2" style="padding:10px 12px;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:8px;">
                <div style="font-size:11px;color:var(--fm-m,#A09888);margin-bottom:6px;"><i class="mdi mdi-bank me-1"></i>Or pay manually via M-Pesa Paybill</div>
                <div style="display:flex;gap:12px;flex-wrap:wrap;font-size:12px;">
                  <span>Paybill <strong style="color:#BFA164;">4160303</strong></span>
                  <span>Account <strong style="color:var(--fm-h,#F5F0E8);">CART-{{ groupState(g.seller_id).orderGroupId.substring(0,8).toUpperCase() }}</strong></span>
                  <span>Amount <strong style="color:#50C878;">KES {{ fNum(groupState(g.seller_id).totalDue || (groupTotals[g.seller_id] && groupTotals[g.seller_id].total) || 0) }}</strong></span>
                </div>
                <button class="btn btn-sm btn-outline-warning mt-2" @click="copyPayLink(groupState(g.seller_id).orderGroupId)"><i class="mdi" :class="payLinkCopied === groupState(g.seller_id).orderGroupId ? 'mdi-check' : 'mdi-link-variant'"></i> {{ payLinkCopied === groupState(g.seller_id).orderGroupId ? 'Link copied!' : 'Copy payment link' }}</button>
              </div>
            </div>
          </div>

          <!-- Client issue 28: forced "Flockr will arrange transport" banner removed — each seller card now has its own transport choice above. -->
        </div>

        <!-- RIGHT: Delivery, Phone, Pay -->
        <div class="col-lg-5">
          <div class="cc-card" style="position:sticky;top:80px;">
            <h6 style="color:#BFA164;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:1px;margin-bottom:14px;">Delivery</h6>
            <div style="position:relative;" class="mb-3">
              <label class="cc-label">Delivery address *</label>
              <div style="display:flex;gap:8px;">
                <div style="flex:1;position:relative;">
                  <input v-model="form.deliveryLocation" class="cc-input" placeholder="Search town, market, area..." @input="onLocSearch" @focus="showLocResults=true" autocomplete="off" style="padding-right:36px;" />
                  <i v-if="form.deliveryLatitude" class="mdi mdi-check-circle" style="position:absolute;right:10px;top:50%;transform:translateY(-50%);color:#50C878;"></i>
                </div>
                <button type="button" class="cc-gps-btn" @click="useMyLocation" :disabled="geoLoading" title="Use my current location">
                  <i v-if="geoLoading" class="mdi mdi-loading mdi-spin"></i>
                  <i v-else class="mdi mdi-crosshairs-gps"></i>
                </button>
              </div>
              <div v-if="showLocResults && locResults.length" class="cc-loc-dropdown">
                <div v-for="(loc,li) in locResults" :key="li" class="cc-loc-item" @click="pickLocation(loc)">
                  <i class="mdi mdi-map-marker" style="color:#BFA164;flex-shrink:0;"></i>
                  <div><div style="color:var(--fm-h,#F5F0E8);font-size:12px;">{{ loc.display_name }}</div><div style="color:var(--fm-s,#6B6356);font-size:10px;">{{ loc.area }}</div></div>
                </div>
              </div>

              <!-- Admin-portal #13: tap-to-pin map — a typed address that was never
                   picked from the dropdown left the order without coordinates. -->
              <div class="cc-map-container mt-2">
                <div id="cartDeliveryMap" class="cc-interactive-map"></div>
                <div class="cc-map-hint"><i class="mdi mdi-gesture-tap me-1"></i>Tap the map to drop a pin</div>
              </div>
              <div v-if="form.deliveryLatitude" style="font-size:11px;color:#50C878;margin-top:6px;">
                <i class="mdi mdi-check-circle me-1"></i>{{ form.deliveryLocation || 'Pin dropped' }}
                <span style="color:var(--fm-s,#6B6356);">({{ Number(form.deliveryLatitude).toFixed(4) }}, {{ Number(form.deliveryLongitude).toFixed(4) }})</span>
              </div>
            </div>

            <div class="mb-3">
              <label class="cc-label">Notes for sellers (optional)</label>
              <textarea v-model="form.buyerNotes" class="cc-input" rows="2" placeholder="Anything sellers should know about your order..."></textarea>
            </div>

            <h6 style="color:#BFA164;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:1px;margin:18px 0 14px;">Payment</h6>
            <div class="mb-3">
              <label class="cc-label">M-Pesa phone *</label>
              <input v-model="form.phone" class="cc-input" placeholder="07XX XXX XXX or 2547XX XXX XXX" />
              <div style="color:var(--fm-s,#6B6356);font-size:11px;margin-top:4px;">You'll get one STK push per seller on this number. Pay each vendor with their own M-Pesa prompt.</div>
            </div>

            <!-- Grand total summary (informational — you actually pay per seller) -->
            <div class="cc-totals">
              <div class="cc-total-row"><span>Items subtotal ({{ groups.length }} seller{{ groups.length===1?'':'s' }})</span><span>KES {{ fNum(itemsSubtotal) }}</span></div>
              <div v-if="insuranceTotal > 0" class="cc-total-row"><span><i class="mdi mdi-shield-check me-1" style="color:#50C878;"></i>Livestock insurance ({{ insureListingIds.length }} animal{{ insureListingIds.length===1?'':'s' }})</span><span style="color:#50C878;">KES {{ fNum(insuranceTotal) }}</span></div>
              <div v-if="productDeliveryTotal > 0" class="cc-total-row"><span><i class="mdi mdi-truck-delivery me-1" style="color:#4FC3F7;"></i>Product delivery (sellers deliver)</span><span style="color:#4FC3F7;">KES {{ fNum(productDeliveryTotal) }}</span></div>
              <div v-if="hasUnavailableProductDelivery" class="cc-total-row" style="font-size:11px;color:#FF9800;"><span><i class="mdi mdi-alert me-1"></i>Some sellers can't deliver to this address</span><span>Pickup may be available</span></div>
              <div v-if="groups.some(g=>g.hasListings && (groupTransport[g.seller_id]||'flockr_arranges')==='flockr_arranges')" class="cc-total-row"><span>Animal transport (Flockr arranges)</span><span style="color:#FF9800;">Invoiced separately</span></div>
              <div v-else-if="groups.some(g=>g.hasListings)" class="cc-total-row"><span>Animal transport (you collect)</span><span style="color:#50C878;">Free</span></div>
              <div class="cc-total-row cc-grand"><span>Cart total</span><span>KES {{ fNum(grandTotal) }}</span></div>
            </div>

            <div style="margin-top:14px;padding:10px 12px;background:rgba(191,161,100,.06);border:1px solid rgba(191,161,100,.2);border-radius:8px;font-size:12px;color:var(--fm-h,#F5F0E8);line-height:1.5;">
              <i class="mdi mdi-information-outline me-1" style="color:#BFA164;"></i>
              <strong>Pay each seller separately</strong> using their "Checkout" button on the left. Each STK push covers that vendor's items plus any add-ons you selected.
            </div>
            <div v-if="allGroupsPaid" class="mt-3" style="padding:10px 12px;background:rgba(80,200,120,.12);border-radius:8px;color:#50C878;font-size:12px;">
              <i class="mdi mdi-check-all me-1"></i>All sellers paid. <a href="javascript:void(0)" @click="$router.push('/orders')" style="color:#50C878;font-weight:600;">View your orders →</a>
            </div>
            <div style="font-size:11px;color:var(--fm-s,#6B6356);text-align:center;margin-top:8px;">
              Secured by escrow. Each seller is paid only after you confirm delivery.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .cc-root{background:#F7F5F2!important}
html[data-theme="light"] .cc-topbar{background:rgba(255,255,255,.95)!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .cc-card,.cc-group,.cc-success-card{background:#FFFFFF!important;border-color:#E8E2DA!important}
</style>

<style scoped>
.cc-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh;color:var(--fm-h,#F5F0E8)}
.cc-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.cc-back{color:var(--fm-m,#A09888);font-size:22px;margin-right:4px;text-decoration:none}
.cc-container{padding-top:80px;padding-bottom:60px;max-width:1100px}
.cc-card,.cc-group,.cc-success-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:18px}
.cc-group{margin-bottom:14px;padding:0;overflow:hidden}
.cc-group-hd{padding:14px 18px;background:var(--fm-bg2,#0F0F0F);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.cc-item{display:flex;justify-content:space-between;align-items:center;padding:12px 18px;border-bottom:1px solid var(--fm-br,#1A1A1A);gap:12px}
.cc-item:last-child{border-bottom:none}
.cc-qty{display:flex;align-items:center;gap:6px;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:6px;padding:2px;flex-shrink:0}
.cc-qty button{width:26px;height:26px;background:transparent;border:none;color:#BFA164;cursor:pointer;display:flex;align-items:center;justify-content:center;border-radius:4px}
.cc-qty button:hover:not(:disabled){background:rgba(191,161,100,.12)}
.cc-qty button:disabled{opacity:.3;cursor:not-allowed}
.cc-qty span{min-width:24px;text-align:center;color:var(--fm-h,#F5F0E8);font-weight:600;font-size:13px}
.cc-avatar{width:36px;height:36px;border-radius:50%;background:var(--fm-bg3,#1E1E1E);display:flex;align-items:center;justify-content:center;font-weight:700;color:#BFA164;flex-shrink:0;overflow:hidden}
.cc-avatar img{width:100%;height:100%;object-fit:cover}
.cc-label{display:block;color:var(--fm-m,#A09888);font-size:11px;font-weight:600;margin-bottom:4px}
.cc-input{width:100%;padding:10px 12px;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:6px;color:var(--fm-h,#F5F0E8);font-size:13px;outline:none}
.cc-input:focus{border-color:#BFA164}
.cc-gps-btn{width:42px;height:42px;display:flex;align-items:center;justify-content:center;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:6px;color:#BFA164;cursor:pointer;flex-shrink:0}
.cc-loc-dropdown{position:absolute;left:0;right:50px;top:calc(100% + 4px);background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:6px;max-height:240px;overflow-y:auto;z-index:10;box-shadow:0 8px 20px rgba(0,0,0,.4)}
.cc-map-container{position:relative;border-radius:10px;overflow:hidden;border:1px solid var(--fm-br,#1E1E1E)}
.cc-interactive-map{width:100%;height:200px;background:var(--fm-bg2,#0F0F0F);z-index:1}
.cc-map-hint{position:absolute;bottom:8px;left:50%;transform:translateX(-50%);background:rgba(10,10,10,.85);border:1px solid var(--fm-br,#1E1E1E);border-radius:20px;padding:4px 14px;font-size:11px;color:var(--fm-m,#A09888);z-index:10;pointer-events:none;white-space:nowrap}
.cc-loc-item{display:flex;gap:8px;padding:10px 12px;cursor:pointer;border-bottom:1px solid var(--fm-br,#1A1A1A)}
.cc-loc-item:hover{background:rgba(191,161,100,.08)}
.cc-totals{margin-top:18px;padding-top:14px;border-top:1px solid var(--fm-br,#1E1E1E)}
.cc-total-row{display:flex;justify-content:space-between;font-size:13px;color:var(--fm-m,#A09888);margin-bottom:6px}
.cc-total-row.cc-grand{font-size:16px;font-weight:700;color:var(--fm-h,#F5F0E8);padding-top:10px;margin-top:10px;border-top:1px solid var(--fm-br,#1E1E1E)}
.cc-total-row.cc-grand span:last-child{color:#BFA164}
.cc-alert{background:rgba(255,107,107,.1);border:1px solid rgba(255,107,107,.3);color:#FF6B6B;padding:10px 14px;border-radius:6px;font-size:13px;margin-bottom:14px}
.cc-success-card{max-width:520px;margin:0 auto 24px;text-align:left}
.cc-success-line{display:flex;justify-content:space-between;font-size:12px;padding:6px 0;border-bottom:1px solid var(--fm-br,#1A1A1A);color:var(--fm-m,#A09888)}
.cc-success-line:last-child{border-bottom:none}
.cc-mono{font-family:monospace;color:#BFA164;font-size:11px}

/* Post-checkout payment panel */
.cc-post{max-width:620px;margin:0 auto}
.cc-pay-panel{padding:22px}
.cc-pulse-row{display:flex;align-items:center;justify-content:center;gap:8px;margin-bottom:8px}
.cc-pulse{width:10px;height:10px;border-radius:50%;background:#50C878;box-shadow:0 0 0 0 rgba(80,200,120,.6);animation:cc-pulse 1.6s infinite}
@keyframes cc-pulse{0%{box-shadow:0 0 0 0 rgba(80,200,120,.6)}70%{box-shadow:0 0 0 12px rgba(80,200,120,0)}100%{box-shadow:0 0 0 0 rgba(80,200,120,0)}}
.cc-retry-box{background:rgba(191,161,100,.06);border:1px solid rgba(191,161,100,.2);border-radius:8px;padding:14px;max-width:380px;margin:0 auto}
.cc-paybill{padding-top:16px;border-top:1px solid var(--fm-br,#1E1E1E)}
.cc-paybill-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
.cc-paybill-grid > div{background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:6px;padding:10px;display:flex;flex-direction:column;gap:2px}
.cc-pb-lbl{color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;letter-spacing:.5px}
.cc-pb-val{color:var(--fm-h,#F5F0E8);font-weight:600;font-size:13px}

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  .cc-container { padding-top: 70px; padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)); padding-left: 12px; padding-right: 12px; }
  .cc-card, .cc-success-card { padding: 14px; border-radius: 10px; }
  .cc-group-hd { padding: 10px 14px; }
  .cc-item { padding: 10px 14px; gap: 8px; }
  .cc-paybill-grid { grid-template-columns: repeat(2, 1fr); }
  .cc-paybill-grid > div { padding: 8px; }
  .cc-pb-val { font-size: 12px; }
  .cc-retry-box { padding: 10px; }
}
</style>

<script>
/* eslint-disable */
import { execute, executeJson, flocker, uploads } from "@/api";
export default {
  name: "CartCheckout",
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null,loading:true,submitting:false,success:null,error:'',
    items:[],groups:[],itemCount:0,itemsSubtotal:0,qtyBusy:false,
    // Insurance choices stashed by Cart.vue before navigation
    insureListingIds: [], insuranceQuotes: {},
    // Product delivery quotes — keyed by seller_id, computed when buyer fills delivery location
    productDeliveryQuotes: {}, productQuotesLoading: false,
    form:{deliveryLocation:'',deliveryLatitude:null,deliveryLongitude:null,phone:'',buyerNotes:''},
    locResults:[],showLocResults:false,locSearchTimer:null,geoLoading:false,
    mapInstance:null,deliveryMarker:null,
    // Payment status tracking (polled while in success state)
    payStatus:'pending', payFailReason:'', mpesaReceipt:'',
    payRequestId:'', payCheckoutRequestId:'', payPaymentRef:'', payPhone:'',
    pollTimer:null, pollCountdown:5,
    retryPhone:'', retryingStk:false, payLinkCopied:'',
    // Split-payment state — for carts > KES 150,000 that need multiple M-Pesa prompts
    splitTotal: 1, splitSeq: 1, amountThisChunk: 0,
    totalDue: 0, paidSoFar: 0,
    awaitingNextChunk: false, payingNext: false,
    stkCeiling: 150000,
    // Per-seller checkout state. Each seller group gets its own STK + escrow.
    // Pilot reviewer (Mr Dhimand Shah) asked for this so buyers can pay each
    // vendor independently and reconcile per-vendor terms separately.
    // Keyed by seller_id → { status, paymentRef, requestId, ckRequestId, mpesaReceipt,
    //                        payFailReason, pollTimer, pollCountdown, splitTotal,
    //                        splitSeq, amountThisChunk, totalDue, paidSoFar,
    //                        awaitingNextChunk, orderGroupId }
    groupPayState: {},
    // Per-seller selected payment method: 'mpesa' (default) or 'on_account'.
    // Only seller cards whose corporate_terms exist let the buyer flip to on_account.
    groupPayMethod: {},
    // Per-seller animal transport choice: 'flockr_arranges' (default) or 'buyer_collects'.
    groupTransport: {}
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    paidPercent(){ return this.totalDue > 0 ? Math.min(100, Math.round((this.paidSoFar / this.totalDue) * 100)) : 0; },
    // Client issue 27: buyer pays the listed price only — the 3% platform fee is
    // deducted from the seller's payout server-side.
    insuranceTotal(){
      var sum = 0; var qs = this.insuranceQuotes; var ids = this.insureListingIds;
      for (var i = 0; i < ids.length; i++) {
        var lid = ids[i];
        if (qs[lid] && qs[lid].available) sum += Number(qs[lid].premium || 0);
      }
      return sum;
    },
    productDeliveryTotal(){
      var sum = 0; var qs = this.productDeliveryQuotes;
      for (var sid in qs) {
        if (qs[sid] && qs[sid].canDeliver) sum += Number(qs[sid].fee || 0);
      }
      return sum;
    },
    hasUnavailableProductDelivery(){
      var qs = this.productDeliveryQuotes;
      for (var sid in qs) { if (qs[sid] && qs[sid].canDeliver === false) return true; }
      return false;
    },
    grandTotal(){return this.itemsSubtotal + this.insuranceTotal + this.productDeliveryTotal;},
    // Per-seller totals for the per-group "Checkout" buttons.
    // Returns a map: seller_id → { subtotal, insurance, delivery, total }
    groupTotals(){
      var out = {}; var qs = this.insuranceQuotes; var ids = this.insureListingIds;
      var pdq = this.productDeliveryQuotes;
      this.groups.forEach(function(g){
        var sub = Number(g.subtotal || 0);
        var ins = 0;
        g.items.forEach(function(it){
          var lid = it.listing_id || it.item_id;
          if (lid && ids.indexOf(lid) !== -1 && qs[lid] && qs[lid].available) {
            ins += Number(qs[lid].premium || 0);
          }
        });
        var del = (pdq[g.seller_id] && pdq[g.seller_id].canDeliver) ? Number(pdq[g.seller_id].fee || 0) : 0;
        out[g.seller_id] = { subtotal: sub, insurance: ins, delivery: del, total: sub + ins + del };
      });
      return out;
    },
    allGroupsPaid(){
      var st = this.groupPayState;
      if (!this.groups.length) return false;
      return this.groups.every(function(g){
        var s = st[g.seller_id] && st[g.seller_id].status;
        return s === 'success' || s === 'invoiced';
      });
    },
    anyGroupInFlight(){
      var st = this.groupPayState;
      return this.groups.some(function(g){
        var s = st[g.seller_id] && st[g.seller_id].status;
        return s === 'submitting' || s === 'stk_sent' || s === 'chunk_succeeded';
      });
    },
    canSubmit(){return this.form.deliveryLocation && this.form.deliveryLatitude && this.form.phone && this.form.phone.replace(/\D/g,'').length >= 9;},
    retryPhoneValid(){return this.retryPhone && this.retryPhone.replace(/\D/g,'').length >= 9;},
    accountRef(){return this.success && this.success.orderGroupId ? ('CART-' + this.success.orderGroupId.substring(0,8)) : '';}
  },
  mounted(){
    var t=localStorage.getItem('flockr_theme')||'dark';window.__flockrSetTheme(t);
    this.darkMode=t!=='light';this.syncTheme();
    this.themeObserver=new MutationObserver(this.syncTheme);
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    // Hydrate insurance choices stashed by Cart.vue
    try {
      this.insureListingIds = JSON.parse(sessionStorage.getItem('flockr_cart_insure_ids') || '[]');
      this.insuranceQuotes  = JSON.parse(sessionStorage.getItem('flockr_cart_insure_quotes') || '{}');
    } catch (e) {
      this.insureListingIds = []; this.insuranceQuotes = {};
    }
    this.load();
  },
  beforeUnmount(){if(this.themeObserver)this.themeObserver.disconnect();if(this.locSearchTimer)clearTimeout(this.locSearchTimer);this.stopPolling()},
  watch: {
    'form.deliveryLatitude'(v){ if (v) this.refreshProductQuotes(); }
  },
  methods:{
    syncTheme(){this.darkMode=document.documentElement.getAttribute('data-theme')!=='light'},
    copyPayLink(groupId){
      if(!groupId) return;
      var self=this;
      navigator.clipboard.writeText(window.location.origin+'/pay/'+groupId).then(function(){ self.payLinkCopied=groupId; setTimeout(function(){ self.payLinkCopied=''; },2500); });
    },
    // Client issue 29: quantity steppers at checkout. Single animals stay fixed at 1.
    canEditQty(it){
      if (it.item_type === 'listing') return it.listing_type === 'batch';
      return true;
    },
    async changeQty(it, newQty){
      if (newQty < 1) return;
      if (it.available_quantity && newQty > Number(it.available_quantity)) return;
      this.qtyBusy = true;
      try{
        var r = await executeJson({function:'updateCartQuantity', userId:sessionStorage.getItem('userId'), cartItemId:it.cart_item_id, quantity:newQty}, flocker);
        if (r.data && r.data.success) {
          it.quantity = newQty;
          this.groups.forEach(function(g){
            g.subtotal = g.items.reduce(function(s,i){return s + Number(i.unit_price)*Number(i.quantity);},0);
          });
          this.itemsSubtotal = this.groups.reduce(function(s,g){return s+g.subtotal;},0);
        } else {
          this.error = (r.data && r.data.message) || 'Failed to update quantity';
        }
      }catch(e){ this.error = 'Failed to update quantity'; }
      this.qtyBusy = false;
    },
    fNum(n){return Number(n||0).toLocaleString()},
    // Seller avatars come back as relative paths like "uploads/avatars/xxx.jpg".
    // Prefix with the configured uploads base so the <img> resolves.
    avatarUrl(u){
      if (!u) return '';
      var s = String(u);
      if (s.indexOf('http') === 0) return s;
      return uploads + s.replace(/^uploads\//, '');
    },
    async refreshProductQuotes(){
      // Pull a delivery quote per seller-with-products from the server using buyer's lat/lng
      var sellersWithProducts = {};
      this.groups.forEach(function(g){
        if (g.hasProducts) sellersWithProducts[g.seller_id] = true;
      });
      var sids = Object.keys(sellersWithProducts);
      if (!sids.length || !this.form.deliveryLatitude) return;
      this.productQuotesLoading = true;
      var fresh = {};
      for (var i = 0; i < sids.length; i++) {
        try {
          var r = await executeJson({
            function: 'calculateProductDeliveryFee',
            sellerId: sids[i],
            lat: this.form.deliveryLatitude,
            lng: this.form.deliveryLongitude
          }, flocker);
          if (r.data && r.data.success) fresh[sids[i]] = r.data.data || {};
          else fresh[sids[i]] = { canDeliver:false, reason: (r.data && r.data.message) || 'Could not get quote' };
        } catch(e){ fresh[sids[i]] = { canDeliver:false, reason:'Network error' }; }
      }
      this.productDeliveryQuotes = fresh;
      this.productQuotesLoading = false;
    },
    async load(){
      this.loading=true;
      var userId=sessionStorage.getItem('userId');
      if(!userId){this.$router.push('/login');return}
      try{
        var r=await executeJson({function:'getCart',userId:userId},flocker);
        if(r.data&&r.data.success&&r.data.data){
          this.items=r.data.data.items||[];
          // Both listings (animals) and products in cart — listings ship via Flockr-arranged
          // transport, products ship via the seller's own delivery (Jiji-style).
          this.groups=(r.data.data.groups||[]).map(function(g){
            g.subtotal = g.items.reduce(function(s,i){return s + Number(i.unit_price)*Number(i.quantity);},0);
            g.hasProducts = g.items.some(function(i){return i.item_type === 'product';});
            g.hasListings = g.items.some(function(i){return i.item_type === 'listing';});
            return g;
          });
          // Default each seller's payment method (corporate_terms may be present from API).
          var pm = {}; var tr = {};
          this.groups.forEach(function(g){ pm[g.seller_id] = 'mpesa'; tr[g.seller_id] = 'flockr_arranges'; });
          this.groupPayMethod = pm;
          this.groupTransport = tr;
          this.itemCount=this.groups.reduce(function(s,g){return s+g.items.length;},0);
          this.itemsSubtotal=this.groups.reduce(function(s,g){return s+g.subtotal;},0);
        }
      }catch(e){console.error(e)}
      // Prefill the M-Pesa phone from the buyer's profile (they can still override)
      if(!this.form.phone){
        try{
          var pr=await executeJson({function:'getUserProfile',userId:userId,viewerId:sessionStorage.getItem('userId')||''},flocker);
          if(pr.data&&pr.data.success){var u=pr.data.data.user||pr.data.data||{};if(u.phone)this.form.phone=u.phone}
        }catch(e){}
      }
      this.loading=false;
      this.$nextTick(this.initCartMap);
    },
    onLocSearch(){
      if(this.locSearchTimer)clearTimeout(this.locSearchTimer);
      var self=this;var q=String(this.form.deliveryLocation||'').trim();
      if(q.length<3){this.locResults=[];this.showLocResults=false;return}
      this.locSearchTimer=setTimeout(function(){self.doLocSearch(q)},400);
    },
    async doLocSearch(q){
      try{
        var url='https://nominatim.openstreetmap.org/search?format=json&countrycodes=ke&limit=6&q='+encodeURIComponent(q);
        var res=await fetch(url);var data=await res.json();
        this.locResults=data.map(function(d){var parts=(d.display_name||'').split(',');return{display_name:parts.slice(0,3).join(', ').trim(),area:parts.slice(1,4).join(', ').trim(),lat:d.lat,lon:d.lon}});
        this.showLocResults=true;
      }catch(e){this.locResults=[]}
    },
    pickLocation(loc){
      this.form.deliveryLocation=loc.display_name;
      this.form.deliveryLatitude=Number(loc.lat);this.form.deliveryLongitude=Number(loc.lon);
      this.locResults=[];this.showLocResults=false;
      this.updateMapPin(Number(loc.lat),Number(loc.lon));
    },
    useMyLocation(){
      if(!navigator.geolocation)return;
      this.geoLoading=true;var self=this;
      navigator.geolocation.getCurrentPosition(
        function(pos){
          self.form.deliveryLatitude=pos.coords.latitude;self.form.deliveryLongitude=pos.coords.longitude;
          self.updateMapPin(pos.coords.latitude,pos.coords.longitude);
          fetch('https://nominatim.openstreetmap.org/reverse?format=json&lat='+pos.coords.latitude+'&lon='+pos.coords.longitude)
            .then(function(r){return r.json()}).then(function(d){
              if(d&&d.display_name){var parts=d.display_name.split(',');self.form.deliveryLocation=parts.slice(0,3).join(',').trim()}
              self.geoLoading=false;
            }).catch(function(){self.geoLoading=false});
        },
        function(){self.geoLoading=false},
        {enableHighAccuracy:true,timeout:10000}
      );
    },
    loadLeaflet(cb){
      if(window.L){cb();return}
      var css=document.createElement('link');css.rel='stylesheet';css.href='https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';document.head.appendChild(css);
      var js=document.createElement('script');js.src='https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';js.onload=cb;document.head.appendChild(js);
    },
    initCartMap(){
      var self=this;
      this.loadLeaflet(function(){
        self.$nextTick(function(){
          var el=document.getElementById('cartDeliveryMap');
          if(!el)return;
          if(self.mapInstance){self.mapInstance.invalidateSize();return}
          var hasPin=!!self.form.deliveryLatitude;
          var lat=Number(self.form.deliveryLatitude)||-1.2921, lon=Number(self.form.deliveryLongitude)||36.8219;
          self.mapInstance=window.L.map('cartDeliveryMap',{zoomControl:true,attributionControl:false}).setView([lat,lon],hasPin?13:6);
          window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',{maxZoom:18}).addTo(self.mapInstance);
          self.mapInstance.on('click',function(e){
            self.form.deliveryLatitude=e.latlng.lat;self.form.deliveryLongitude=e.latlng.lng;
            self.updateMapPin(e.latlng.lat,e.latlng.lng);
            self.reverseGeocodePin(e.latlng.lat,e.latlng.lng);
          });
          if(hasPin)self.updateMapPin(lat,lon);
        });
      });
    },
    updateMapPin(lat,lon){
      if(!this.mapInstance||!window.L)return;
      if(this.deliveryMarker){this.deliveryMarker.setLatLng([lat,lon]);}
      else{
        var icon=window.L.divIcon({className:'cc-delivery-pin',html:'<div style="background:#BFA164;width:16px;height:16px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.5);"></div>',iconSize:[16,16],iconAnchor:[8,8]});
        this.deliveryMarker=window.L.marker([lat,lon],{icon:icon,draggable:true}).addTo(this.mapInstance).bindTooltip('Delivery location',{direction:'top'});
        var self=this;
        this.deliveryMarker.on('dragend',function(e){
          var p=e.target.getLatLng();
          self.form.deliveryLatitude=p.lat;self.form.deliveryLongitude=p.lng;
          self.reverseGeocodePin(p.lat,p.lng);
        });
      }
      this.mapInstance.setView([lat,lon],Math.max(this.mapInstance.getZoom(),13));
    },
    reverseGeocodePin(lat,lon){
      var self=this;
      fetch('https://nominatim.openstreetmap.org/reverse?format=json&lat='+lat+'&lon='+lon)
        .then(function(r){return r.json()}).then(function(d){
          if(d&&d.display_name){var parts=d.display_name.split(',');self.form.deliveryLocation=parts.slice(0,3).join(',').trim()}
        }).catch(function(){});
    },
    async submit(){
      if(!this.canSubmit){this.error='Please complete delivery location and M-Pesa phone';return}
      this.error='';this.submitting=true;
      var userId=sessionStorage.getItem('userId');
      try{
        var r=await executeJson({
          function:'createCartCheckout',
          userId:userId,
          deliveryLocation:this.form.deliveryLocation,
          deliveryLatitude:this.form.deliveryLatitude||'',
          deliveryLongitude:this.form.deliveryLongitude||'',
          phone:this.form.phone,
          buyerNotes:this.form.buyerNotes||'',
          // Auto-policy: list of listing IDs the buyer ticked "Protect this animal" for.
          // Server re-computes the actual premium so we can't be tricked into paying less.
          insureListingIds: JSON.stringify(this.insureListingIds || [])
        },flocker);
        if(r.data && r.data.success && r.data.data){
          this.success=r.data.data;
          // Capture payment tracking handles
          this.payRequestId = r.data.data.requestId || '';
          this.payCheckoutRequestId = r.data.data.checkoutRequestId || '';
          this.payPaymentRef = r.data.data.paymentRef || '';
          this.payPhone = r.data.data.phone || this.form.phone;
          this.retryPhone = (r.data.data.phone || this.form.phone || '').replace(/^254/, '');
          // Split-payment metadata (server tells us if this got split into N chunks)
          this.splitTotal = r.data.data.splitTotal || 1;
          this.splitSeq = r.data.data.splitSeq || 1;
          this.amountThisChunk = r.data.data.amountThisChunk || r.data.data.amount || 0;
          this.totalDue = r.data.data.totalDue || r.data.data.grandTotal || 0;
          this.paidSoFar = r.data.data.paidSoFar || 0;
          this.awaitingNextChunk = false;
          this.payStatus = 'stk_sent';
          this.startPolling();
          // Clear local cart cache + insurance stash + bump topbar badge
          sessionStorage.setItem('flockr_cart_count',0);
          sessionStorage.removeItem('flockr_cart_insure_ids');
          sessionStorage.removeItem('flockr_cart_insure_quotes');
          window.dispatchEvent(new CustomEvent('flockr-cart-updated',{detail:{count:0}}));
        } else {
          this.error=(r.data && r.data.message) || 'Checkout failed';
        }
      }catch(e){
        this.error=(e.response&&e.response.data&&e.response.data.message)||'Network error — please try again';
      }
      this.submitting=false;
    },

    // After the last payment clears, take the buyer to the order so they can
    // follow progress instead of idling on checkout. Short delay lets the
    // green "Paid" state register first.
    redirectToOrders(orderIds){
      var dest = (orderIds && orderIds.length === 1) ? '/orders/' + orderIds[0] : '/orders';
      var self = this;
      setTimeout(function(){ self.$router.push(dest); }, 2000);
    },
    // ===== Payment polling =====
    startPolling(){
      this.stopPolling();
      this.pollCountdown = 5;
      var self = this;
      this.pollTimer = setInterval(function(){
        self.pollCountdown--;
        if (self.pollCountdown <= 0) { self.pollCountdown = 5; self.checkPayment(); }
      }, 1000);
    },
    stopPolling(){
      if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
    },
    async checkPayment(){
      if (!this.payRequestId && !this.payCheckoutRequestId && !this.payPaymentRef) return;
      try {
        var f = new FormData(); f.append('function','checkPaymentStatus');
        if (this.payRequestId) f.append('requestId', this.payRequestId);
        if (this.payCheckoutRequestId) f.append('checkoutRequestId', this.payCheckoutRequestId);
        if (this.payPaymentRef) f.append('paymentRef', this.payPaymentRef);
        var r = await execute(f, flocker);
        if (r.data && r.data.data) {
          var st = r.data.data.status;
          var msg = r.data.data.message || '';
          if (st === 'success' || st === 'completed') {
            this.stopPolling();
            this.mpesaReceipt = r.data.data.paymentRef || r.data.data.mpesaReceipt || '';
            // Bump the local paid tally for THIS chunk, then decide: more chunks or fully paid?
            this.paidSoFar = Math.min(this.totalDue, this.paidSoFar + Number(this.amountThisChunk || 0));
            if (this.splitSeq < this.splitTotal && this.paidSoFar + 0.01 < this.totalDue) {
              // More chunks to go — show "Pay Next" prompt instead of full success
              this.awaitingNextChunk = true;
              this.payStatus = 'chunk_succeeded';
              this.amountThisChunk = Math.min(this.stkCeiling, this.totalDue - this.paidSoFar);
            } else {
              // Final chunk — full success
              this.payStatus = 'success';
              this.awaitingNextChunk = false;
              this.redirectToOrders((this.success && this.success.orders || []).map(function(o){ return o.id; }));
            }
          } else if (st === 'cancelled') {
            this.payStatus = 'cancelled'; this.payFailReason = msg; this.stopPolling();
          } else if (st === 'failed') {
            this.payStatus = 'failed'; this.payFailReason = msg; this.stopPolling();
          } else if (st === 'timeout') {
            this.payStatus = 'timeout'; this.payFailReason = msg || 'No PIN entered in 5 minutes'; this.stopPolling();
          }
        }
      } catch(e) {}
    },
    // For split payments: fire the next chunk in the plan when the buyer taps "Pay Next"
    async payNext(){
      if (this.payingNext || !this.success || !this.success.orderGroupId) return;
      this.payingNext = true;
      try {
        var r = await executeJson({ function: 'payNextSplit', orderGroupId: this.success.orderGroupId }, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.payRequestId = r.data.data.stkRequestId || '';
          this.payCheckoutRequestId = r.data.data.checkoutRequestId || '';
          this.payPaymentRef = '';
          this.amountThisChunk = r.data.data.amountThisChunk || this.amountThisChunk;
          this.splitSeq = r.data.data.splitSeq || (this.splitSeq + 1);
          this.splitTotal = r.data.data.splitTotal || this.splitTotal;
          this.awaitingNextChunk = false;
          this.payStatus = 'stk_sent';
          this.payFailReason = '';
          this.startPolling();
        } else {
          this.payFailReason = (r.data && r.data.message) || 'Could not send next prompt';
          this.payStatus = 'failed';
        }
      } catch(e) {
        this.payFailReason = (e.response && e.response.data && e.response.data.message) || 'Network error';
        this.payStatus = 'failed';
      }
      this.payingNext = false;
    },
    async resendStk(){
      if (!this.retryPhoneValid) return;
      this.retryingStk = true;
      var userId = sessionStorage.getItem('userId');
      var phoneRaw = this.retryPhone.replace(/\D/g, '');
      // accept either "07.." or "7..": always normalize to 2547..
      var phone = phoneRaw.charAt(0) === '0' ? ('254' + phoneRaw.substring(1))
                : (phoneRaw.length === 9 ? ('254' + phoneRaw) : phoneRaw);
      try {
        var r = await executeJson({
          function: 'retryCartPayment',
          userId: userId,
          orderGroupId: this.success.orderGroupId,
          phone: phone
        }, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.payRequestId = r.data.data.requestId || '';
          this.payCheckoutRequestId = r.data.data.checkoutRequestId || '';
          this.payPaymentRef = r.data.data.paymentRef || '';
          this.payPhone = r.data.data.phone || phone;
          this.payStatus = 'stk_sent';
          this.payFailReason = '';
          this.startPolling();
        } else {
          this.payFailReason = (r.data && r.data.message) || 'Could not resend the M-Pesa prompt';
          this.payStatus = 'failed';
        }
      } catch(e) {
        this.payFailReason = (e.response && e.response.data && e.response.data.message) || 'Network error — please try again';
        this.payStatus = 'failed';
      }
      this.retryingStk = false;
    },

    // ===== Per-seller checkout (new flow) =====
    canSubmitGroup(){
      // Same prerequisites as the combined Pay button — delivery + phone needed.
      return this.canSubmit;
    },
    groupState(sellerId){
      return this.groupPayState[sellerId] || { status: 'idle' };
    },
    groupBtnLabel(g){
      var s = this.groupState(g.seller_id).status;
      var t = (this.groupTotals[g.seller_id] || {}).total || 0;
      var lbl = g.seller_name || 'Seller';
      var method = this.groupPayMethod[g.seller_id] || 'mpesa';
      if (s === 'submitting') return method === 'on_account' ? 'Creating invoice…' : 'Initiating M-Pesa…';
      if (s === 'stk_sent') return 'Awaiting M-Pesa PIN…';
      if (s === 'chunk_succeeded') return 'Tap to send next prompt';
      if (s === 'success') return '✓ Paid ' + lbl;
      if (s === 'invoiced') return '✓ Invoice issued';
      if (s === 'failed' || s === 'cancelled' || s === 'timeout') {
        return method === 'on_account'
          ? 'Retry · Issue invoice to ' + lbl
          : 'Retry · Checkout ' + lbl + ' — KES ' + this.fNum(t);
      }
      if (method === 'on_account') {
        var terms = g.corporate_terms ? g.corporate_terms.payment_terms_days : 0;
        return 'Issue ' + terms + '-day invoice to ' + lbl + ' — KES ' + this.fNum(t);
      }
      return 'Checkout ' + lbl + ' — KES ' + this.fNum(t);
    },
    groupBtnClass(g){
      var s = this.groupState(g.seller_id).status;
      if (s === 'success') return 'btn-success';
      if (s === 'failed' || s === 'cancelled' || s === 'timeout') return 'btn-outline-warning';
      return 'btn-warning';
    },
    groupBtnDisabled(g){
      var s = this.groupState(g.seller_id).status;
      if (s === 'submitting' || s === 'stk_sent' || s === 'success') return true;
      return !this.canSubmitGroup();
    },
    async submitGroup(g){
      var method = this.groupPayMethod[g.seller_id] || 'mpesa';
      var sellerId = g.seller_id;
      // M-Pesa requires phone + delivery; on_account only requires delivery.
      if (!this.form.deliveryLocation || !this.form.deliveryLatitude) { this.error = 'Please pick a delivery address'; return; }
      if (method === 'mpesa' && (!this.form.phone || this.form.phone.replace(/\D/g,'').length < 9)) {
        this.error = 'Please complete M-Pesa phone'; return;
      }
      this.error = '';
      // Initialize / reset this group's payment state
      this.groupPayState = Object.assign({}, this.groupPayState, {
        [sellerId]: { status: 'submitting', sellerId: sellerId, pollCountdown: 5, splitTotal: 1, splitSeq: 1,
                      amountThisChunk: 0, totalDue: 0, paidSoFar: 0, awaitingNextChunk: false,
                      paymentRef: '', requestId: '', ckRequestId: '', mpesaReceipt: '', payFailReason: '',
                      orderGroupId: '' }
      });
      var userId = sessionStorage.getItem('userId');
      // Only send insurance flags relevant to THIS seller's items
      var sellerListingIds = (g.items || []).map(function(it){ return it.listing_id; }).filter(Boolean);
      var insureForThisSeller = (this.insureListingIds || []).filter(function(id){ return sellerListingIds.indexOf(id) !== -1; });
      try {
        var r = await executeJson({
          function: 'createSellerCheckout',
          userId: userId,
          sellerId: sellerId,
          deliveryLocation: this.form.deliveryLocation,
          deliveryLatitude: this.form.deliveryLatitude || '',
          deliveryLongitude: this.form.deliveryLongitude || '',
          phone: this.form.phone || '',
          paymentMethod: method,
          transportOption: this.groupTransport[sellerId] || 'flockr_arranges',
          buyerNotes: this.form.buyerNotes || '',
          insureListingIds: JSON.stringify(insureForThisSeller)
        }, flocker);
        if (r.data && r.data.success && r.data.data) {
          var d = r.data.data;
          this.groupPayState[sellerId].orderGroupId      = d.orderGroupId || '';
          this.groupPayState[sellerId].orderIds          = d.orderIds || [];
          // On-account branch — no STK, just an invoice
          if (d.paymentMethod === 'on_account' || method === 'on_account') {
            this.groupPayState[sellerId].invoiceNumber   = d.invoiceNumber || '';
            this.groupPayState[sellerId].invoiceId       = d.invoiceId || '';
            this.groupPayState[sellerId].dueDate         = d.dueDate || '';
            this.groupPayState[sellerId].invoiceTotal    = d.invoiceTotal || 0;
            this.groupPayState[sellerId].totalDue        = d.invoiceTotal || d.sellerTotal || 0;
            this.groupPayState[sellerId].paidSoFar       = 0;
            this.groupPayState[sellerId].status          = 'invoiced';
            this.markGroupPending(sellerId);
            return;
          }
          // M-Pesa branch — pick up STK polling handles
          this.groupPayState[sellerId].paymentRef        = d.paymentRef || '';
          this.groupPayState[sellerId].requestId         = d.requestId || '';
          this.groupPayState[sellerId].ckRequestId       = d.checkoutRequestId || '';
          this.groupPayState[sellerId].splitTotal        = d.splitTotal || 1;
          this.groupPayState[sellerId].splitSeq          = d.splitSeq || 1;
          this.groupPayState[sellerId].amountThisChunk   = d.amountThisChunk || d.amount || 0;
          this.groupPayState[sellerId].totalDue          = d.totalDue || d.sellerTotal || 0;
          this.groupPayState[sellerId].paidSoFar         = d.paidSoFar || 0;
          this.groupPayState[sellerId].status            = 'stk_sent';
          this.startPollingGroup(sellerId);
          // Optimistic UI: items are in-flight, not yet paid.
          this.markGroupPending(sellerId);
        } else {
          this.groupPayState[sellerId].status        = 'failed';
          this.groupPayState[sellerId].payFailReason = (r.data && r.data.message) || 'Checkout failed';
          this.unmarkGroupPending(sellerId);
        }
      } catch (e) {
        this.groupPayState[sellerId].status        = 'failed';
        this.groupPayState[sellerId].payFailReason = (e && e.message) || 'Network error';
        this.unmarkGroupPending(sellerId);
      }
    },
    // Bug fix 2026-07-16 — was called `removeGroupItems` and marked items as
    // `_paid` at STK-send time. That was misleading (STK hadn't confirmed yet) and
    // caused the "✓ Paid" label to lie whenever the payment later timed out or
    // was cancelled, which stranded the buyer with a Retry button on a group they
    // couldn't re-checkout (cart was already emptied server-side). Now we mark
    // items as `_pending` (STK in flight) here, and only flip to `_paid = true`
    // when the poll returns success (see checkPaymentGroup).
    markGroupPending(sellerId){
      this.groups = this.groups.map(function(g){
        if (g.seller_id === sellerId) {
          g.items = g.items.map(function(it){ it._pending = true; it._paid = false; return it; });
        }
        return g;
      });
      sessionStorage.setItem('flockr_cart_count', this.groups.filter(function(g){ return !g.items.every(function(i){ return i._pending || i._paid; }); }).reduce(function(s,g){ return s + g.items.length; }, 0));
      window.dispatchEvent(new CustomEvent('flockr-cart-updated', { detail: { count: Number(sessionStorage.getItem('flockr_cart_count') || 0) } }));
    },
    markGroupPaid(sellerId){
      this.groups = this.groups.map(function(g){
        if (g.seller_id === sellerId) {
          g.items = g.items.map(function(it){ it._paid = true; it._pending = false; return it; });
        }
        return g;
      });
    },
    unmarkGroupPending(sellerId){
      // On failure / timeout / cancel, drop the _pending flag so the visual doesn't
      // keep implying the payment is in flight or done.
      this.groups = this.groups.map(function(g){
        if (g.seller_id === sellerId) {
          g.items = g.items.map(function(it){ if (!it._paid) it._pending = false; return it; });
        }
        return g;
      });
    },
    startPollingGroup(sellerId){
      this.stopPollingGroup(sellerId);
      var self = this;
      this.groupPayState[sellerId].pollCountdown = 5;
      this.groupPayState[sellerId]._pollTimer = setInterval(function(){
        var st = self.groupPayState[sellerId];
        if (!st) { clearInterval(arguments.callee); return; }
        st.pollCountdown--;
        if (st.pollCountdown <= 0) { st.pollCountdown = 5; self.checkPaymentGroup(sellerId); }
      }, 1000);
    },
    stopPollingGroup(sellerId){
      var st = this.groupPayState[sellerId];
      if (st && st._pollTimer) { clearInterval(st._pollTimer); st._pollTimer = null; }
    },
    async checkPaymentGroup(sellerId){
      var st = this.groupPayState[sellerId];
      if (!st) return;
      if (!st.requestId && !st.ckRequestId && !st.paymentRef) return;
      try {
        var f = new FormData(); f.append('function', 'checkPaymentStatus');
        if (st.requestId)   f.append('requestId', st.requestId);
        if (st.ckRequestId) f.append('checkoutRequestId', st.ckRequestId);
        if (st.paymentRef)  f.append('paymentRef', st.paymentRef);
        var r = await execute(f, flocker);
        if (r.data && r.data.data) {
          var s = r.data.data.status, msg = r.data.data.message || '';
          if (s === 'success' || s === 'completed') {
            this.stopPollingGroup(sellerId);
            st.mpesaReceipt = r.data.data.paymentRef || r.data.data.mpesaReceipt || '';
            st.paidSoFar    = Math.min(st.totalDue, (st.paidSoFar || 0) + Number(st.amountThisChunk || 0));
            if (st.splitSeq < st.splitTotal && st.paidSoFar + 0.01 < st.totalDue) {
              st.awaitingNextChunk = true;
              st.status            = 'chunk_succeeded';
              st.amountThisChunk   = Math.min(this.stkCeiling, st.totalDue - st.paidSoFar);
            } else {
              st.status = 'success';
              st.awaitingNextChunk = false;
              // Real success — flip items to ✓ Paid (was previously set at STK-send time).
              this.markGroupPaid(sellerId);
              if (this.allGroupsPaid) {
                var gps = this.groupPayState, allIds = [];
                Object.keys(gps).forEach(function(k){ (gps[k].orderIds || []).forEach(function(id){ allIds.push(id); }); });
                this.redirectToOrders(allIds);
              }
              // GA4 purchase event (Alex 2026-07-23). Fires once per seller group
              // when the final chunk clears — mirrors gtag ecommerce spec.
              if (this.$trackEvent) {
                var group = this.groups.find(function(g){ return g.seller_id === sellerId; });
                var items = group ? group.items.map(function(it){
                  return { item_id: it.listing_id || it.product_id, item_name: it.title, price: Number(it.unit_price)||0, quantity: Number(it.quantity)||1 };
                }) : [];
                this.$trackEvent('purchase', {
                  transaction_id: st.orderGroupId || sellerId,
                  currency: (group && group.currency) || 'KES',
                  value: Number(st.totalDue) || 0,
                  items: items
                });
              }
            }
          } else if (s === 'cancelled') {
            st.status = 'cancelled'; st.payFailReason = msg; this.stopPollingGroup(sellerId);
            this.unmarkGroupPending(sellerId);
          } else if (s === 'failed') {
            st.status = 'failed';    st.payFailReason = msg; this.stopPollingGroup(sellerId);
            this.unmarkGroupPending(sellerId);
          } else if (s === 'timeout') {
            st.status = 'timeout';   st.payFailReason = msg || 'No PIN entered in 5 minutes'; this.stopPollingGroup(sellerId);
            this.unmarkGroupPending(sellerId);
          }
        }
      } catch(e) {}
    },
    async payNextGroupChunk(sellerId){
      var st = this.groupPayState[sellerId];
      if (!st || !st.orderGroupId) return;
      try {
        var r = await executeJson({ function: 'payNextSplit', orderGroupId: st.orderGroupId }, flocker);
        if (r.data && r.data.success && r.data.data) {
          st.requestId        = r.data.data.stkRequestId || '';
          st.ckRequestId      = r.data.data.checkoutRequestId || '';
          st.paymentRef       = '';
          st.amountThisChunk  = r.data.data.amountThisChunk || st.amountThisChunk;
          st.splitSeq         = r.data.data.splitSeq || (st.splitSeq + 1);
          st.splitTotal       = r.data.data.splitTotal || st.splitTotal;
          st.awaitingNextChunk = false;
          st.status           = 'stk_sent';
          st.payFailReason    = '';
          this.startPollingGroup(sellerId);
        } else {
          st.payFailReason = (r.data && r.data.message) || 'Could not send next prompt';
          st.status        = 'failed';
        }
      } catch(e) {
        st.payFailReason = e.message || 'Network error';
        st.status        = 'failed';
      }
    }
  }
};
</script>
